import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageHeader, Card, DifficultyBadge, useCategoryLabel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, ArrowLeft, Phone, PhoneOff, User, Headphones, Award } from "lucide-react";

export default function LiveCall() {
  const { t, lang } = useLang();
  const catLabel = useCategoryLabel();
  const [scenarios, setScenarios] = useState([]);
  const [active, setActive] = useState(null);
  const [turnIdx, setTurnIdx] = useState(0);
  const [messages, setMessages] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [mistakes, setMistakes] = useState([]);
  const [done, setDone] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { api.get("/live-call/scenarios").then((r) => setScenarios(r.data)); }, []);
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, feedback]);

  const start = (s) => {
    setActive(s); setTurnIdx(0); setScore(0); setMistakes([]); setDone(false); setFeedback(null);
    setMessages([{ side: "caller", text: lang === "en" ? s.turns[0].caller_en : s.turns[0].caller_hi }]);
  };

  const choose = (opt, i) => {
    if (feedback) return;
    const turn = active.turns[turnIdx];
    setMessages((m) => [...m, { side: "agent", text: lang === "en" ? opt.en : opt.hi }]);
    setFeedback({ correct: opt.correct, text: lang === "en" ? opt.fb_en : opt.fb_hi });
    if (opt.correct) setScore((s) => s + 1);
    else setMistakes((ms) => [...ms, lang === "en" ? turn.caller_en.slice(0, 40) : "गलत प्रतिक्रिया"]);
  };

  const nextTurn = async () => {
    setFeedback(null);
    const nextIdx = turnIdx + 1;
    if (nextIdx >= active.turns.length) {
      setDone(true);
      await api.post("/sessions", {
        module: "live_call", scenario_id: active.scenario_id, category_code: active.category_code,
        score, max_score: active.turns.length, mistakes, details: {},
      });
      return;
    }
    setTurnIdx(nextIdx);
    setMessages((m) => [...m, { side: "caller", text: lang === "en" ? active.turns[nextIdx].caller_en : active.turns[nextIdx].caller_hi }]);
  };

  if (active) {
    const turn = active.turns[turnIdx];
    return (
      <div className="max-w-3xl">
        <Button variant="ghost" onClick={() => setActive(null)} className="mb-4" data-testid="live-back">
          <ArrowLeft className="h-4 w-4 mr-2" /> {t("back")}
        </Button>
        <Card className="p-0 overflow-hidden">
          <div className="bg-[#0F172A] text-white px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-emerald-400 animate-pulse" />
              <span className="font-medium text-sm">{lang === "en" ? active.title_en : active.title_hi}</span>
            </div>
            <span className="text-xs text-slate-400">{catLabel(active.category_code)}</span>
          </div>

          <div ref={scrollRef} className="h-[380px] overflow-y-auto p-5 space-y-4 bg-slate-50" data-testid="live-chat">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.side === "agent" ? "justify-end" : "justify-start"}`}>
                {m.side === "caller" && <div className="h-8 w-8 rounded-full bg-slate-300 flex items-center justify-center shrink-0"><User className="h-4 w-4 text-slate-600" /></div>}
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                  m.side === "agent" ? "bg-[#0F172A] text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
                }`}>{m.text}</div>
                {m.side === "agent" && <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center shrink-0"><Headphones className="h-4 w-4 text-white" /></div>}
              </div>
            ))}
            {feedback && (
              <div className={`rounded-md border p-3 text-sm animate-fade-in ${feedback.correct ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="live-feedback">
                <div className="flex items-center gap-2 font-medium mb-1">
                  {feedback.correct ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  {feedback.correct ? t("correct") : t("incorrect")}
                </div>
                {feedback.text}
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 p-4 bg-white">
            {done ? (
              <div className="text-center py-4 animate-fade-in" data-testid="live-review">
                <Award className="h-10 w-10 text-amber-500 mx-auto mb-2" />
                <div className="font-head font-bold text-lg text-slate-900">{t("review")}</div>
                <div className="text-2xl font-bold text-blue-700 my-2">{score} / {active.turns.length}</div>
                <p className="text-sm text-slate-600 max-w-md mx-auto mb-4">{lang === "en" ? active.review_en : active.review_hi}</p>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" onClick={() => start(active)} data-testid="live-retry">{t("start")}</Button>
                  <Button className="bg-[#0F172A] hover:bg-slate-800" onClick={() => setActive(null)}>{t("finish")}</Button>
                </div>
              </div>
            ) : feedback ? (
              <Button onClick={nextTurn} className="w-full bg-[#0F172A] hover:bg-slate-800" data-testid="live-next">
                {turnIdx + 1 >= active.turns.length ? t("finish") : t("next")}
              </Button>
            ) : (
              <div className="space-y-2">
                <div className="text-xs font-medium text-slate-400 uppercase mb-1">{t("selectAnswer")}</div>
                {turn.options.map((o, i) => (
                  <button key={i} data-testid={`live-option-${i}`} onClick={() => choose(o, i)}
                    className="w-full text-left rounded-md border-2 border-slate-300 hover:border-blue-500 hover:bg-blue-50 px-4 py-2.5 text-sm text-slate-800 transition-colors">
                    {lang === "en" ? o.en : o.hi}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={t("liveCall")} subtitle={t("scenarios")} />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {scenarios.map((s) => (
          <Card key={s.scenario_id} className="hover:shadow-md transition-shadow" data-testid={`live-card-${s.scenario_id}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-slate-500">{catLabel(s.category_code)}</span>
              <DifficultyBadge level={s.difficulty} />
            </div>
            <h3 className="font-head font-semibold text-slate-900 mb-2">{lang === "en" ? s.title_en : s.title_hi}</h3>
            <p className="text-sm text-slate-500 mb-4">{lang === "en" ? s.intro_en : s.intro_hi}</p>
            <Button onClick={() => start(s)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`live-start-${s.scenario_id}`}>
              <Phone className="h-4 w-4 mr-2" /> {t("start")}
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
}
